import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../api';
import Template1 from '../components/Template1/Template1';
import Template2 from '../components/Template2/Template2';
import Template3 from '../components/Template3/Template3';
import Template4 from '../components/Template4/Template4';
import Template5 from '../components/Template5/Template5';
import Template6 from '../components/Template6/Template6';
import Template7 from '../components/Template7/Template7';
import Template8 from '../components/Template8/Template8';
import Template9 from '../components/Template9/Template9';
import { LocalBusinessSchema } from '../components/SchemaScript';
import { generateCompleteSEO } from '../utils/seo';
import SEOHead from '../components/SEOHead';

function renderTemplate(id, real) {
  const n = Number(id);
  if (n === 1) return <Template1 templateData={real} />;
  if (n === 2) return <Template2 templateData={real} />;
  if (n === 3) return <Template3 templateData={real} />;
  if (n === 4) return <Template4 templateData={real} />;
  if (n === 5) return <Template5 templateData={real} />;
  if (n === 6) return <Template6 templateData={real} />;
  if (n === 7) return <Template7 templateData={real} />;
  if (n === 8) return <Template8 templateData={real} />;
  if (n === 9) return <Template9 templateData={real} />;
  return <Template1 templateData={real} />;
}

export default function BusinessView() {
  const { slug } = useParams();
  const [listing, setListing] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/v1/directory/listing/${encodeURIComponent(slug)}`);
        if (!res.ok) throw new Error(`Listing not found (${res.status})`);
        const j = await res.json();
        setListing(j);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (error) return <div style={{ padding: 24, color: '#b00020' }}>Error: {error}</div>;
  if (!listing) return <div style={{ padding: 24 }}>Not found</div>;

  // If listing has template_data and template_id, render the template directly
  if (listing.template_data && listing.template_id) {
    try {
      const templateData = typeof listing.template_data === 'string'
        ? JSON.parse(listing.template_data)
        : listing.template_data;
      const real = templateData?.template?.realData || templateData?.templateData || templateData?.realData || {};

      if (real && Object.keys(real).length > 0) {
        // Build schema data from template data
        const schemaData = {
          name: real.businessName || real.name || listing.name,
          slug: slug,
          description: real.about || real.description || '',
          phone: real.phone || real.phoneNumber || '',
          email: real.email || '',
          streetAddress: real.address || '',
          city: real.city || listing.city || '',
          state: real.state || listing.state || '',
          postalCode: real.postalCode || real.zipCode || '',
          country: real.country || 'IN',
          latitude: real.latitude || listing.latitude,
          longitude: real.longitude || listing.longitude,
          logo: real.logo || real.businessLogo || '',
          images: real.photos || real.images || [],
          rating: real.rating || listing.rating,
          reviewCount: real.reviewCount || listing.review_count,
          reviews: real.reviews || real.testimonials || [],
          services: real.services || [],
          faqs: real.faqs || [],
          openingHours: real.openingHours || real.businessHours || [],
          socialProfiles: [
            real.facebook,
            real.instagram,
            real.twitter,
            real.linkedin,
            real.youtube
          ].filter(Boolean),
          businessType: real.businessType || 'LocalBusiness'
        };

        const businessName = schemaData.name;
        const businessCity = schemaData.city;
        const businessState = schemaData.state;

        const seoData = generateCompleteSEO({
          title: businessName,
          description: schemaData.description || `${businessName} in ${businessCity}, ${businessState}. Contact us for ${real.services?.[0]?.title || 'services'}. ${schemaData.phone}`,
          keywords: [
            businessName,
            `${businessName} ${businessCity}`,
            real.businessType || 'business',
            ...(real.services?.slice(0, 5).map(s => s.title) || [])
          ],
          url: `/${slug}`,
          type: 'business.business',
          image: schemaData.logo
        });

        return (
          <div style={{ minHeight: '100vh', background: '#fff' }}>
            <SEOHead
              title={seoData.title}
              description={seoData.meta.description}
              keywords={seoData.meta.keywords}
              canonical={seoData.link.canonical}
              robots="index, follow"
              ogTitle={seoData.meta['og:title']}
              ogDescription={seoData.meta['og:description']}
              ogUrl={seoData.meta['og:url']}
              ogType="business.business"
              ogImage={schemaData.logo}
              twitterTitle={seoData.meta['twitter:title']}
              twitterDescription={seoData.meta['twitter:description']}
              twitterImage={schemaData.logo}
              geoPosition={schemaData.latitude ? `${schemaData.latitude};${schemaData.longitude}` : null}
              extraMeta={{
                'business:contact_data:phone_number': schemaData.phone,
                'business:contact_data:email': schemaData.email,
                'business:contact_data:street_address': schemaData.streetAddress,
                'business:contact_data:locality': businessCity,
                'business:contact_data:region': businessState
              }}
            />

            <LocalBusinessSchema business={schemaData} />
            {renderTemplate(listing.template_id, real)}
          </div>
        );
      }
    } catch (e) {
      console.error('Failed to parse template data:', e);
    }
  }

  // Fallback: if website URL exists and is external, redirect to it
  const website = String(listing.website || '');
  if (website && website !== '' && !website.includes('/live-demo/')) {
    const url = /^https?:\/\//i.test(website) ? website : `https://${website}`;
    window.location.replace(url);
    return <div style={{ padding: 24 }}>Redirecting…</div>;
  }

  // No valid website data found
  return (
    <div style={{ padding: 24 }}>
      <h2>Website Not Available</h2>
      <p>This business listing does not have a website configured yet.</p>
    </div>
  );
}


